// src/services/profileService.js - Using Firebase methods directly
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc,
  getDocs, 
  query, 
  serverTimestamp,
  where, 
  orderBy,
  limit 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { firestoreService } from './firestoreService';
import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

export const profileService = {
  // Get all sections for a profile
  async getProfileSections(bookId) {
    try {
      const sectionsRef = collection(db, 'pb-books', bookId, 'pb-sections');
      const q = query(sectionsRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const sections = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return sections;
    } catch (error) {
      console.error('Error getting profile sections:', error);
      throw error;
    }
  },

  // Get a specific section
  async getProfileSection(bookId, sectionId) {
    try {
      const sectionRef = doc(db, 'pb-books', bookId, 'pb-sections', sectionId);
      const docSnap = await getDoc(sectionRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Section not found');
      }
    } catch (error) {
      console.error('Error getting profile section:', error);
      throw error;
    }
  },

  // Add a new section
  async addProfileSection(bookId, sectionData) {
    try {
      const sectionsRef = collection(db, 'pb-books', bookId, 'pb-sections');
      const docRef = await addDoc(sectionsRef, {
        ...sectionData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error adding profile section:', error);
      throw error;
    }
  },

  // Update a section
  async updateProfileSection(bookId, sectionId, sectionData) {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'pb-sections', sectionId);
      await updateDoc(docRef, {
        ...sectionData,
        updatedAt: new Date()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating profile section:', error);
      throw error;
    }
  },

  // Delete a section
  async deleteProfileSection(bookId, sectionId) {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'pb-sections', sectionId);
      await deleteDoc(docRef);
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting profile section:', error);
      throw error;
    }
  },

  // Update profile settings (updates the main book document)
  async updateProfileSettings(bookId, settings) {
    try {
      const bookRef = doc(db, 'pb-books', bookId);
      await updateDoc(bookRef, {
        profileSettings: settings,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating profile settings:', error);
      throw error;
    }
  },

  // Get profile interactions
  async getProfileInteractions(bookId, userId = null) {
    try {
      const interactionsRef = collection(db, 'pb-books', bookId, 'pb-interactions');
      let q = query(interactionsRef, orderBy('createdAt', 'desc'), limit(100));
      
      if (userId) {
        q = query(interactionsRef, where('userId', '==', userId));
      }
      
      const querySnapshot = await getDocs(q);
      const interactions = querySnapshot.docs.map(doc => doc.data());

      return {
        liked: interactions.some(i => i.type === 'like' && i.userId === userId),
        viewCount: interactions.filter(i => i.type === 'view').length,
        likeCount: interactions.filter(i => i.type === 'like').length
      };
    } catch (error) {
      console.error('Error getting profile interactions:', error);
      return { liked: false, viewCount: 0, likeCount: 0 };
    }
  },

  // Track profile view
  async trackView(bookId, userId = null) {
    try {
      // In a full implementation, you'd increment view count
      const bookRef = doc(db, 'pb-books', bookId);
      await updateDoc(bookRef, {
        viewCount: serverTimestamp(), // Use increment(1) when you add viewCount field
        lastViewed: serverTimestamp()
      });
      
      console.log('Tracking view for profile:', bookId);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  },
  
  // Update interaction (like, share, etc.)
  async updateInteraction(bookId, userId, interactionType, data = {}) {
    try {
      if (!userId) {
        throw new Error('User ID required for interactions');
      }

      const interactionsRef = collection(db, 'pb-books', bookId, 'pb-interactions');
      
      // Check if interaction already exists (for likes)
      if (interactionType === 'like') {
        const q = query(
          interactionsRef, 
          where('userId', '==', userId),
          where('type', '==', 'like')
        );
        const existingSnapshot = await getDocs(q);
        
        if (!existingSnapshot.empty) {
          // Remove existing like
          await deleteDoc(existingSnapshot.docs[0].ref);
          return { action: 'removed', type: interactionType };
        }
      }

      // Add new interaction
      await addDoc(interactionsRef, {
        type: interactionType,
        userId,
        bookId,
        data,
        createdAt: new Date()
      });
      
      return { action: 'added', type: interactionType };
    } catch (error) {
      console.error('Error updating interaction:', error);
      throw error;
    }
  },

  async getProfileSections(bookId) {
    try {
      const sectionsRef = collection(db, 'pb-books', bookId, 'pb-sections');
      const q = query(sectionsRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting profile sections:', error);
      throw error;
    }
  },

  // Get a specific section
  async getProfileSection(bookId, sectionId) {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'pb-sections', sectionId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting profile section:', error);
      throw error;
    }
  },

  // Add a new section
  async addProfileSection(bookId, sectionData) {
    try {
      const sectionsRef = collection(db, 'pb-books', bookId, 'pb-sections');
      const docRef = await addDoc(sectionsRef, {
        ...sectionData,
        bookId,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      return { id: docRef.id, ...sectionData };
    } catch (error) {
      console.error('Error adding profile section:', error);
      throw error;
    }
  },

  // Update a section
  async updateProfileSection(bookId, sectionId, sectionData) {
    try {
      const sectionRef = doc(db, 'pb-books', bookId, 'pb-sections', sectionId);
      await updateDoc(sectionRef, {
        ...sectionData,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating profile section:', error);
      throw error;
    }
  },

  // Delete a section
  async deleteProfileSection(bookId, sectionId) {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'pb-sections', sectionId);
      await deleteDoc(docRef);
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting profile section:', error);
      throw error;
    }
  },

  // Update profile settings (updates the main book document)
  async updateProfileSettings(bookId, settings) {
    try {
      const bookRef = doc(db, 'pb-books', bookId);
      await updateDoc(bookRef, {
        profileSettings: settings,
        updatedAt: new Date()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating profile settings:', error);
      throw error;
    }
  },

  // Get profile section statistics
  async getProfileSectionStats(bookId) {
    try {
      const sectionsRef = collection(db, 'pb-books', bookId, 'pb-sections');
      const querySnapshot = await getDocs(sectionsRef);
      
      const stats = {
        total: 0,
        byType: {},
        byStatus: {},
        published: 0,
        draft: 0,
        totalViews: 0,
        totalInteractions: 0
      };

      querySnapshot.docs.forEach(doc => {
        const section = doc.data();
        stats.total++;

        // Count by type
        const type = section.type || 'unknown';
        stats.byType[type] = (stats.byType[type] || 0) + 1;

        // Count by status
        const status = section.status || 'draft';
        stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;

        if (status === 'published') stats.published++;
        if (status === 'draft') stats.draft++;

        // Add view counts if available
        stats.totalViews += section.views || 0;
        stats.totalInteractions += section.interactions || 0;
      });

      return stats;
    } catch (error) {
      console.error('Error getting profile section stats:', error);
      return {
        total: 0,
        byType: {},
        byStatus: {},
        published: 0,
        draft: 0,
        totalViews: 0,
        totalInteractions: 0
      };
    }
  },

  // Rest of the methods using the same subcollection pattern...
  async getProfileInteractions(bookId, userId = null) {
    try {
      const interactionsRef = collection(db, 'pb-books', bookId, 'pb-interactions');
      let q = query(interactionsRef, orderBy('createdAt', 'desc'), limit(100));
      
      if (userId) {
        q = query(interactionsRef, where('userId', '==', userId));
      }
      
      const querySnapshot = await getDocs(q);
      const interactions = querySnapshot.docs.map(doc => doc.data());

      return {
        liked: interactions.some(i => i.type === 'like' && i.userId === userId),
        viewCount: interactions.filter(i => i.type === 'view').length,
        likeCount: interactions.filter(i => i.type === 'like').length
      };
    } catch (error) {
      console.error('Error getting profile interactions:', error);
      return { liked: false, viewCount: 0, likeCount: 0 };
    }
  },

  async trackView(bookId, userId = null) {
    try {
      const interactionsRef = collection(db, 'pb-books', bookId, 'pb-interactions');
      await addDoc(interactionsRef, {
        type: 'view',
        userId: userId || 'anonymous',
        bookId,
        createdAt: new Date(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
      });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  },

  async uploadProfileMedia(file, bookId, sectionType, itemIndex = null) {
    try {

      // Validate file type and size
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }

      const allowedTypes = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf', 'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error(`File type ${file.type} not supported. Allowed types: images, PDF, Word documents`);
      }

      // Create unique filename with proper path structure
      const timestamp = Date.now();
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const itemPath = itemIndex !== null ? `item-${itemIndex}` : 'main';
      const fileName = `profile-books/${bookId}/profile/${sectionType}/${itemPath}/${timestamp}_${cleanFileName}`;

      console.log(`Uploading file to: ${fileName}`);

      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log(`Upload successful: ${downloadURL}`);

      return {
        url: downloadURL,
        fileName: cleanFileName,
        fileType: file.type,
        uploadedAt: new Date().toISOString(),
        path: fileName
      };
    } catch (error) {
      console.error('Error uploading profile media:', error);
      
      // Provide user-friendly error messages
      if (error.code === 'storage/unauthorized') {
        throw new Error('Upload permission denied. Please check your access rights.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload was canceled');
      } else if (error.code === 'storage/quota-exceeded') {
        throw new Error('Storage quota exceeded');
      } else if (error.code === 'storage/invalid-argument') {
        throw new Error('Invalid file or upload parameters');
      } else if (error.message.includes('Firebase ID token has expired')) {
        throw new Error('Session expired. Please refresh the page and try again.');
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  },

  async deleteProfileMedia(mediaUrl) {
    try {
      if (mediaUrl && mediaUrl.includes('firebase')) {
        const mediaRef = ref(storage, mediaUrl);
        await deleteObject(mediaRef);
      }
      return { success: true };
    } catch (error) {
      console.error('Error deleting profile media:', error);
      return { success: false, error: error.message };
    }
  },

  async uploadMultipleFiles(files, bookId, sectionType, onProgress) {
    const uploadPromises = files.map(async (file, index) => {
      try {
        const result = await this.uploadProfileMedia(file, bookId, sectionType, index);
        
        if (onProgress) {
          onProgress(index + 1, files.length);
        }
        
        return { success: true, index, ...result };
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        return { success: false, index, error: error.message, fileName: file.name };
      }
    });

    return await Promise.all(uploadPromises);
  },

  async trackEngagement(bookId, engagementType, data) {
    try {
      const engagementRef = collection(db, 'pb-books', bookId, 'pb-engagements');
      
      // Check if user already has this engagement
      if (data.userId) {
        const existingQ = query(
          engagementRef,
          where('userId', '==', data.userId),
          where('type', '==', engagementType)
        );
        const existingDocs = await getDocs(existingQ);
        
        if (existingDocs.docs.length > 0 && !['view', 'share'].includes(engagementType)) {
          // Toggle engagement for like, bookmark, etc.
          const existingDoc = existingDocs.docs[0];
          await deleteDoc(existingDoc.ref);
          
          // Get new total
          const totalCount = await this.getEngagementCount(bookId, engagementType);
          return { userStatus: false, newTotal: totalCount };
        }
      }

      // Add new engagement
      await addDoc(engagementRef, {
        type: engagementType,
        bookId,
        userId: data.userId || 'anonymous',
        userEmail: data.userEmail || null,
        data: data,
        createdAt: serverTimestamp(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
      });

      // Get new total
      const totalCount = await this.getEngagementCount(bookId, engagementType);
      
      // Send notification to book owner for certain engagement types
      if (['expressed_interest', 'purchase_click'].includes(engagementType)) {
        await this.notifyBookOwner(bookId, engagementType, data);
      }

      return { userStatus: true, newTotal: totalCount };
    } catch (error) {
      console.error('Error tracking engagement:', error);
      throw error;
    }
  },

  async getEngagementData(bookId, userId = null) {
    try {
      const engagementRef = collection(db, 'pb-books', bookId, 'pb-engagements');
      const allEngagements = await getDocs(engagementRef);
      
      const totals = {
        likes: 0,
        shares: 0,
        bookmarks: 0,
        interests: 0,
        endorsements: 0,
        views: 0
      };
      
      const userEngagement = {
        liked: false,
        shared: false,
        bookmarked: false,
        expressed_interest: false,
        endorsed: false
      };

      allEngagements.docs.forEach(doc => {
        const data = doc.data();
        const type = data.type;
        
        // Count totals
        if (type === 'like') totals.likes++;
        else if (type === 'shared') totals.shares++;
        else if (type === 'bookmarked') totals.bookmarks++;
        else if (type === 'expressed_interest') totals.interests++;
        else if (type === 'endorsed') totals.endorsements++;
        else if (type === 'view') totals.views++;

        // Check user engagement
        if (userId && data.userId === userId) {
          if (type === 'like') userEngagement.liked = true;
          else if (type === 'shared') userEngagement.shared = true;
          else if (type === 'bookmarked') userEngagement.bookmarked = true;
          else if (type === 'expressed_interest') userEngagement.expressed_interest = true;
          else if (type === 'endorsed') userEngagement.endorsed = true;
        }
      });

      return { totals, userEngagement };
    } catch (error) {
      console.error('Error getting engagement data:', error);
      return { 
        totals: { likes: 0, shares: 0, bookmarks: 0, interests: 0, endorsements: 0, views: 0 },
        userEngagement: { liked: false, shared: false, bookmarked: false, expressed_interest: false, endorsed: false }
      };
    }
  },

  async getEngagementCount(bookId, engagementType) {
    try {
      const engagementRef = collection(db, 'pb-books', bookId, 'pb-engagements');
      const q = query(engagementRef, where('type', '==', engagementType));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.length;
    } catch (error) {
      console.error('Error getting engagement count:', error);
      return 0;
    }
  },

  async notifyBookOwner(bookId, engagementType, engagementData) {
    try {
      // Get book details
      const bookRef = doc(db, 'pb-books', bookId);
      const bookSnap = await getDoc(bookRef);
      
      if (!bookSnap.exists()) return;
      
      const book = bookSnap.data();
      const ownerId = book.userId;
      
      // Get owner email
      const ownerEmail = await this.getUserEmail(ownerId);
      
      if (!ownerEmail) return;

      // Create notification record
      const notificationRef = collection(db, 'pb-notifications');
      await addDoc(notificationRef, {
        type: 'engagement',
        subType: engagementType,
        bookId,
        bookTitle: book.botName,
        userId: ownerId,
        userEmail: ownerEmail,
        engagementData,
        createdAt: serverTimestamp(),
        read: false
      });

      // For high-value engagements, send email
      if (['expressed_interest', 'purchase_click'].includes(engagementType)) {
        // Could integrate with email service here
        console.log(`High-value engagement: ${engagementType} for book ${bookId}`);
      }
    } catch (error) {
      console.error('Error notifying book owner:', error);
    }
  }
};